import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import { TimeSelect } from '../common/DateTimePicker';
import ConfirmDialog from '../common/ConfirmDialog';

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DAY_VALUES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const PATTERN_OPTIONS = [
  { value: 'daily', label: 'Todos los días' },
  { value: 'weekdays', label: 'Lunes a Viernes' },
  { value: 'saturday', label: 'Sábado' },
  { value: 'sunday', label: 'Domingo' },
  { value: 'specific', label: 'Día específico' },
];

const DURATION_OPTIONS = [60, 120, 180, 300];
function patternLabel(b) {
  const p = b.day_pattern || 'specific';
  if (p === 'daily') return 'Todos los días';
  if (p === 'weekdays') return 'L-V';
  if (p === 'saturday') return 'Sábado';
  if (p === 'sunday') return 'Domingo';
  return b.day ? DAY_NAMES[DAY_VALUES.indexOf(b.day)] : 'Todos los días';
}

export default function BreakSchedule() {
  const [breaks, setBreaks] = useState([]);
  const [spots, setSpots] = useState([]);
  const [jingles, setJingles] = useState([]);
  const [jingleFolders, setJingleFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);

  const fetchData = async () => {
    try {
      const [b, s, j, fj] = await Promise.all([
        api.get('/api/advertising/breaks'),
        api.get('/api/advertising/spots'),
        api.get('/api/media', { params: { category: 'jingle', limit: 500 } }),
        api.get('/api/music/folders', { params: { category: 'jingle' } }),
      ]);
      setBreaks(b.data || []);
      setSpots(s.data || []);
      setJingles(j.data?.files || j.data || []);
      setJingleFolders(fj.data || []);
    } catch (err) {
      console.error('[BreakSchedule] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openNew = () => {
    setEditing({
      id: null,
      name: '',
      scheduled_time: '12:00:00',
      day_pattern: 'daily',
      day: null,
      duration_seconds: 180,
      spot_ids: [],
      jingle_ids: [],
      intro_media_file_id: null,
      outro_media_file_id: null,
      spots_per_break: 1,
    });
    setModalOpen(true);
  };

const openEdit = (b) => {
    const spot_ids = [];
    const jingle_ids = [];
    (b.spots || []).forEach((s) => {
      const sid = typeof s === 'string' ? s : s.spot_id;
      if (typeof s !== 'string' && s.kind === 'jingle') {
        jingle_ids.push(sid);
      } else {
        spot_ids.push(sid);
      }
    });
    setEditing({
      id: b.id,
      name: b.name || '',
      scheduled_time: b.scheduled_time,
      day_pattern: b.day_pattern || 'specific',
      day: b.day || null,
      duration_seconds: b.duration_seconds || 180,
      spot_ids,
      jingle_ids,
      intro_media_file_id: b.intro_media_file_id || null,
      outro_media_file_id: b.outro_media_file_id || null,
      spots_per_break: b.spots_per_break != null ? b.spots_per_break : 1,
    });
    setModalOpen(true);
  };

  const toggleJingle = (jid) => {
    setEditing((prev) => ({
      ...prev,
      jingle_ids: prev.jingle_ids.includes(jid) ? prev.jingle_ids.filter((x) => x !== jid) : [...prev.jingle_ids, jid],
    }));
  };

  const addSpot = (sid) => {
    setEditing((prev) => ({
      ...prev,
      spot_ids: prev.spot_ids.includes(sid) ? prev.spot_ids : [...prev.spot_ids, sid],
    }));
  };

  const removeSpot = (idx) => {
    setEditing((prev) => ({
      ...prev,
      spot_ids: prev.spot_ids.filter((_, i) => i !== idx),
    }));
  };

  const moveSpot = (idx, dir) => {
    setEditing((prev) => {
      const arr = [...prev.spot_ids];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= arr.length) return prev;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return { ...prev, spot_ids: arr };
    });
  };

  const jingleFolderName = (fid) => (jingleFolders.find((f) => f.id === fid)?.name) || '';

  const selectedDuration = () => {
    const spotsDur = editing.spot_ids.reduce((acc, sid) => {
      const s = spots.find((x) => x.id === sid);
      return acc + (s?.duration_seconds || 0);
    }, 0);
    const jinglesDur = editing.jingle_ids.reduce((acc, jid) => {
      const j = jingles.find((x) => x.id === jid);
      return acc + (j?.duration_seconds || 0);
    }, 0);
    return spotsDur + jinglesDur;
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        name: editing.name || `Tanda ${editing.scheduled_time}`,
        scheduled_time: editing.scheduled_time,
        day_pattern: editing.day_pattern,
        day: editing.day_pattern === 'specific' ? editing.day : null,
        intro_media_file_id: editing.intro_media_file_id || null,
        outro_media_file_id: editing.outro_media_file_id || null,
        spots_per_break: editing.spots_per_break,
      };
      let id = editing.id;
      if (id) {
        await api.put(`/api/advertising/breaks/${id}`, payload);
      } else {
        const res = await api.post('/api/advertising/breaks', payload);
        id = res.data.id;
      }
      const items = [
        ...editing.spot_ids.map((id) => ({ type: 'spot', id })),
        ...editing.jingle_ids.map((id) => ({ type: 'jingle', id })),
      ];
      await api.put(`/api/advertising/breaks/${id}/items`, { items });
      setModalOpen(false);
      setEditing(null);
      fetchData();
    } catch (err) {
      console.error('[BreakSchedule] Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!confirmItem) return;
    try {
      await api.delete(`/api/advertising/breaks/${confirmItem.id}`);
      setBreaks(breaks.filter((b) => b.id !== confirmItem.id));
    } catch (err) {
      console.error('[BreakSchedule] Delete failed:', err);
    } finally {
      setConfirmOpen(false);
      setConfirmItem(null);
    }
  };

  if (loading) {
    return <div className="flex min-h-[300px] items-center justify-center"><p className="text-dark-400">Cargando tandas...</p></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">Tandas publicitarias</h1>
          <p className="text-sm text-dark-400 mt-1">Elegí la hora, el día y qué publicidades suenan en cada tanda</p>
        </div>
        <div className="flex gap-2">
          <Link to="/advertising/spots" className="btn btn-secondary btn-sm">Spots</Link>
          <button type="button" onClick={openNew} className="btn btn-primary btn-sm">+ Nueva tanda</button>
        </div>
      </div>

      {breaks.length === 0 ? (
        <div className="py-12 text-center text-dark-400">No hay tandas. Creá una con el horario y los spots que quieras.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl bg-dark-850 border border-dark-700">
          <table className="table">
            <thead>
              <tr>
                <th>Hora</th>
                <th>Día</th>
                <th>Duración</th>
                <th>Publicidades</th>
                <th>Activa</th>
                <th className="text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {breaks
                .slice()
                .sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || ''))
                .map((b) => (
                  <tr key={b.id}>
                    <td className="font-mono text-sm text-dark-200">{b.scheduled_time?.slice(0, 5)}</td>
                    <td className="text-sm text-dark-300">{patternLabel(b)}</td>
                    <td className="text-sm text-dark-400">
                      {b.duration_seconds ? `${b.duration_seconds}s` : '—'}
                    </td>
                    <td className="text-sm text-dark-300">
                      {!b.spots || b.spots.length === 0
                        ? <span className="text-dark-500">(aleatorio de campañas activas)</span>
                        : (() => {
                            const nSpots = b.spots.filter((s) => typeof s !== 'string' && s.kind === 'jingle').length;
                            const nAds = b.spots.length - nSpots;
                            const per = (b.spots_per_break || 0) > 0 ? Math.min(nAds, b.spots_per_break) : nAds;
                            const pool = nAds > per;
                            return (
                              <span>
                                {per} publicidad(es) por tanda
                                {pool ? <span className="text-dark-500"> · aleatoria de {nAds}</span> : ''}
                                {nSpots > 0 ? ` + ${nSpots} jingle(s)` : ''}
                              </span>
                            );
                          })()}
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={async () => {
                          await api.put(`/api/advertising/breaks/${b.id}`, { is_active: !b.is_active });
                          fetchData();
                        }}
                        className={`text-xs px-2 py-0.5 rounded ${b.is_active ? 'bg-success-900/30 text-success-400' : 'bg-dark-700 text-dark-500'}`}
                      >
                        {b.is_active ? '● Activa' : '○ Inactiva'}
                      </button>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button type="button" onClick={() => openEdit(b)} className="btn btn-secondary btn-sm mr-1">✎ Editar</button>
                      <button type="button" onClick={() => { setConfirmItem(b); setConfirmOpen(true); }} className="btn btn-error btn-sm">✕</button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal nueva/editar tanda */}
      {modalOpen && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-xl bg-dark-800 p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-dark-100 mb-4">
              {editing.id ? 'Editar tanda' : 'Nueva tanda'}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-1">Hora</label>
                  <TimeSelect
                    value={editing.scheduled_time?.slice(0, 5)}
                    onChange={(v) => setEditing((p) => ({ ...p, scheduled_time: `${v}:00` }))}
                  />
                </div>
                <div className="flex items-end">
                  <p className="text-sm text-dark-300 pb-2">
                    Duración: <span className="text-primary-300 font-medium">
                      {selectedDuration()}s
                    </span> ({editing.spot_ids.length} en pool · suenan {(editing.spots_per_break || 0) > 0 ? Math.min(editing.spot_ids.length, editing.spots_per_break) : editing.spot_ids.length} por tanda + {editing.jingle_ids.length} jingle(s))
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-1">Día</label>
                  <select
                    value={editing.day_pattern}
                    onChange={(e) => setEditing((p) => ({ ...p, day_pattern: e.target.value }))}
                    className="input"
                  >
                    {PATTERN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {editing.day_pattern === 'specific' && (
                  <div>
                    <label className="block text-sm font-medium text-dark-300 mb-1">Día de la semana</label>
                    <select
                      value={editing.day || 'monday'}
                      onChange={(e) => setEditing((p) => ({ ...p, day: e.target.value }))}
                      className="input"
                    >
                      {DAY_NAMES.map((d, i) => <option key={DAY_VALUES[i]} value={DAY_VALUES[i]}>{d}</option>)}
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Publicidades que suenan por tanda</label>
                <input
                  type="number"
                  min={0}
                  value={editing.spots_per_break ?? 1}
                  onChange={(e) => setEditing((p) => ({ ...p, spots_per_break: parseInt(e.target.value, 10) || 0 }))}
                  className="input"
                />
                <p className="text-xs text-dark-500 mt-1">
                  1 = la tanda suena 1 publicidad elegida al azar del listado. 0 = suenan todas las seleccionadas.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Publicidades de esta tanda (pool: {editing.spot_ids.length} seleccionadas)
                </label>
                {spots.length === 0 ? (
                  <p className="text-sm text-dark-500">No hay spots. Creá spots primero (Publicidad → Spots).</p>
                ) : (
                  <div className="space-y-1.5">
                    {editing.spot_ids.length === 0 ? (
                      <p className="text-sm text-dark-500 py-2">Sin publicidades. Agregá desde la lista de abajo.</p>
                    ) : (
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {editing.spot_ids.map((sid, idx) => {
                          const s = spots.find((x) => x.id === sid);
                          if (!s) return null;
                          const noAudio = s.has_audio === false;
                          return (
                            <div key={sid} className="flex items-center gap-2 rounded-md bg-dark-850 border border-dark-700 p-2">
                              <span className="text-xs text-dark-500 w-5 text-right">{idx + 1}.</span>
                              <span className={`flex-1 truncate text-xs ${noAudio ? 'text-error-400' : 'text-dark-100'}`}>
                                {noAudio ? '⚠ ' : ''}{s.name}
                              </span>
                              <div className="flex items-center gap-1">
                                {idx > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => moveSpot(idx, -1)}
                                    className="p-1 rounded hover:bg-dark-600 text-dark-400 hover:text-dark-100"
                                    title="Subir"
                                  >
                                    ▲
                                  </button>
                                )}
                                {idx < editing.spot_ids.length - 1 && (
                                  <button
                                    type="button"
                                    onClick={() => moveSpot(idx, 1)}
                                    className="p-1 rounded hover:bg-dark-600 text-dark-400 hover:text-dark-100"
                                    title="Bajar"
                                  >
                                    ▼
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeSpot(idx)}
                                  className="p-1 rounded hover:bg-error-900/30 text-error-400 hover:text-error-300"
                                  title="Quitar"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="mt-2 pt-2 border-t border-dark-700">
                      <p className="text-xs text-dark-500 mb-1">Disponibles para agregar:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {spots.filter((s) => !editing.spot_ids.includes(s.id)).map((s) => {
                          const noAudio = s.has_audio === false;
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => addSpot(s.id)}
                              disabled={noAudio}
                              title={noAudio ? 'Este spot no tiene audio (se borró su archivo)' : 'Clic para agregar al final'}
                              className={`text-xs px-2 py-1 rounded transition-colors ${noAudio ? 'bg-dark-700/50 text-error-400 border border-error-600/30 cursor-not-allowed' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'}`}
                            >
                              {noAudio ? '⚠ ' : ''}{s.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                <p className="text-xs text-dark-500 mt-2">El listado es el pool de candidatas; el motor elige al azar las que figuran en "por tanda". Si el pool queda vacío, la tanda toma spots aleatorios de campañas activas.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Identificaciones / jingles ({editing.jingle_ids.length} seleccionados)
                </label>
                {jingles.length === 0 ? (
                  <p className="text-sm text-dark-500">No hay jingles cargados (subilos con tipo 🔔 Jingle).</p>
                ) : (
                  <div className="max-h-44 overflow-y-auto rounded-lg bg-dark-900/40 p-2 space-y-1.5">
                    {jingles.map((j) => {
                      const sel = editing.jingle_ids.includes(j.id);
                      const fn = jingleFolderName(j.folder_id);
                      return (
                        <button
                          key={j.id}
                          type="button"
                          onClick={() => toggleJingle(j.id)}
                          className={`w-full flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors ${sel ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'}`}
                        >
                          <span className="truncate">{j.metadata?.title || j.original_filename}</span>
                          {fn && <span className="shrink-0 text-[10px] opacity-70">{fn}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-dark-500 mt-2">Sirve por ejemplo para sonar una identificación de la radio en la tanda.</p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-1">🎬 Identificación al INICIO</label>
                  <select
                    value={editing.intro_media_file_id || ''}
                    onChange={(e) => setEditing((p) => ({ ...p, intro_media_file_id: e.target.value || null }))}
                    className="input text-sm"
                  >
                    <option value="">— Sin identificación —</option>
                    {jingles.map((j) => (
                      <option key={j.id} value={j.id}>{j.metadata?.title || j.original_filename}</option>
                    ))}
                  </select>
                  <p className="text-xs text-dark-500 mt-1">Suena al comienzo de la tanda.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-1">🎬 Identificación al FINAL</label>
                  <select
                    value={editing.outro_media_file_id || ''}
                    onChange={(e) => setEditing((p) => ({ ...p, outro_media_file_id: e.target.value || null }))}
                    className="input text-sm"
                  >
                    <option value="">— Sin identificación —</option>
                    {jingles.map((j) => (
                      <option key={j.id} value={j.id}>{j.metadata?.title || j.original_filename}</option>
                    ))}
                  </select>
                  <p className="text-xs text-dark-500 mt-1">Suena al cierre de la tanda.</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-dark-700 mt-6">
              <button type="button" onClick={() => { setModalOpen(false); setEditing(null); }} className="btn btn-secondary">Cancelar</button>
              <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar tanda'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar tanda"
        message={`¿Seguro que quieres eliminar la tanda de las ${confirmItem?.scheduled_time?.slice(0, 5)}?`}
        danger
        onConfirm={confirmDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
